/**
 * Frontend Errors Dashboard for Admin Panel
 * Shows recent frontend JavaScript errors reported by users
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Bug, 
  RefreshCw, 
  CheckCircle, 
  AlertCircle,
  Globe,
  Monitor,
  User,
  Clock,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Trash2,
  CheckCheck
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FrontendErrorsDashboard = ({ token }) => {
  const [errors, setErrors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showResolved, setShowResolved] = useState(false);
  const [expandedError, setExpandedError] = useState(null);

  const fetchErrors = useCallback(async () => {
    setLoading(true);
    try {
      const [errorsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/errors/frontend/recent?limit=50&resolved=${showResolved ? 'true' : 'false'}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/api/errors/frontend/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (errorsRes.ok) {
        const errorsData = await errorsRes.json();
        setErrors(errorsData.errors || []);
      }
      
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
    } catch (err) {
      console.error('Error fetching frontend errors:', err);
    } finally {
      setLoading(false);
    }
  }, [token, showResolved]);

  useEffect(() => {
    fetchErrors();
  }, [fetchErrors]);

  const resolveError = async (errorId) => {
    try {
      await fetch(`${API_URL}/api/errors/frontend/${errorId}/resolve`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Error resuelto');
      fetchErrors();
    } catch (err) {
      console.error('Error resolving:', err);
      toast.error('Error al resolver');
    }
  };

  const resolveSimilarErrors = async (messageSubstring) => {
    if (!window.confirm(`¿Resolver TODOS los errores que contengan "${messageSubstring.slice(0, 50)}..."?`)) {
      return;
    }
    try {
      const response = await fetch(
        `${API_URL}/api/errors/frontend/resolve-bulk?message_contains=${encodeURIComponent(messageSubstring)}&notes=Resuelto%20en%20masa%20desde%20panel`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();
      toast.success(`${data.resolved_count} errores resueltos`);
      fetchErrors();
    } catch (err) {
      console.error('Error resolving bulk:', err);
      toast.error('Error al resolver en masa');
    }
  };

  const resolveAllExtensionErrors = async () => {
    if (!window.confirm('¿Resolver TODOS los errores de extensiones del navegador (removeChild, insertBefore, etc.)?')) {
      return;
    }
    try {
      const response = await fetch(
        `${API_URL}/api/errors/frontend/resolve-bulk?message_contains=removeChild&notes=Error%20de%20extension%20del%20navegador`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();
      toast.success(`${data.resolved_count} errores de extensiones resueltos`);
      fetchErrors();
    } catch (err) {
      console.error('Error:', err);
      toast.error('Error al resolver');
    }
  };

  const deleteOldErrors = async () => {
    if (!window.confirm('¿Eliminar errores de más de 30 días? Esta acción no se puede deshacer.')) {
      return;
    }
    try {
      const response = await fetch(
        `${API_URL}/api/errors/frontend/delete-old?days_old=30`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();
      toast.success(`${data.deleted_count} errores antiguos eliminados`);
      fetchErrors();
    } catch (err) {
      console.error('Error:', err);
      toast.error('Error al eliminar');
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '-';
    const date = new Date(ts);
    return date.toLocaleString('es-ES', { 
      day: '2-digit', 
      month: '2-digit', 
      year: '2-digit',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getBrowserFromUA = (ua) => {
    if (!ua) return 'Desconocido';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return 'Otro';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Bug className="w-6 h-6 text-red-500" />
          Errores de Frontend
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Bulk action buttons */}
          {!showResolved && stats?.unresolved_errors > 0 && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={resolveAllExtensionErrors}
                className="text-orange-600 border-orange-300 hover:bg-orange-50"
                title="Resolver errores de extensiones (removeChild)"
              >
                <CheckCheck className="w-4 h-4 mr-1" />
                Resolver Extensiones
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={deleteOldErrors}
                className="text-red-600 border-red-300 hover:bg-red-50"
                title="Eliminar errores de más de 30 días"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Limpiar Antiguos
              </Button>
            </>
          )}
          <Button
            variant={showResolved ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowResolved(!showResolved)}
          >
            {showResolved ? 'Mostrando Resueltos' : 'Ver Resueltos'}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchErrors}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Errores</p>
                  <p className="text-2xl font-bold">{stats.total_errors || 0}</p>
                </div>
                <Bug className="w-8 h-8 text-gray-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className={stats.unresolved_errors > 0 ? 'bg-red-50' : ''}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Sin Resolver</p>
                  <p className={`text-2xl font-bold ${stats.unresolved_errors > 0 ? 'text-red-600' : ''}`}>
                    {stats.unresolved_errors || 0}
                  </p>
                </div>
                <AlertCircle className={`w-8 h-8 ${stats.unresolved_errors > 0 ? 'text-red-500' : 'text-gray-400'} opacity-50`} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div>
                <p className="text-sm text-gray-500 mb-2">Top Páginas con Error</p>
                {stats.by_url?.slice(0, 3).map((item, idx) => (
                  <div key={idx} className="text-xs flex justify-between">
                    <span className="truncate max-w-[100px]">{item.url}</span>
                    <span className="font-semibold">{item.count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div>
                <p className="text-sm text-gray-500 mb-2">Usuarios Afectados</p>
                {stats.by_user?.slice(0, 3).map((item, idx) => (
                  <div key={idx} className="text-xs flex justify-between">
                    <span className="truncate max-w-[100px]">{item.email || 'Anónimo'}</span>
                    <span className="font-semibold">{item.count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Errors List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {showResolved ? 'Errores Resueltos' : 'Errores Pendientes'} ({errors.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {errors.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500 opacity-50" />
              <p>{showResolved ? 'No hay errores resueltos' : '¡Sin errores pendientes! 🎉'}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {errors.map((error, idx) => (
                <div 
                  key={idx} 
                  className={`border rounded-lg p-4 ${error.resolved ? 'bg-gray-50' : 'bg-white'}`}
                >
                  {/* Error Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant={error.resolved ? 'secondary' : 'destructive'}>
                          {error.resolved ? 'Resuelto' : 'Pendiente'}
                        </Badge>
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {error.error_id}
                        </code>
                      </div>
                      <p className="font-medium text-red-700 mb-1">
                        {error.message?.slice(0, 100)}{error.message?.length > 100 ? '...' : ''}
                      </p>
                      <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTimestamp(error.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Globe className="w-3 h-3" />
                          {error.url}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {error.user_email || 'Anónimo'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Monitor className="w-3 h-3" />
                          {getBrowserFromUA(error.user_agent)} | {error.screen_size || '?'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {!error.resolved && (
                        <>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => resolveError(error.error_id)}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Resolver
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-orange-600 border-orange-300 hover:bg-orange-50"
                            onClick={() => resolveSimilarErrors(error.message?.split(':')[0] || error.message)}
                            title="Resolver todos los errores con el mismo mensaje"
                          >
                            <CheckCheck className="w-4 h-4 mr-1" />
                            Similares
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setExpandedError(expandedError === idx ? null : idx)}
                      >
                        {expandedError === idx ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedError === idx && (
                    <div className="mt-4 pt-4 border-t space-y-3">
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-1">Stack Trace:</p>
                        <pre className="text-xs bg-gray-100 p-3 rounded overflow-auto max-h-40 text-red-600">
                          {error.stack || 'No stack trace available'}
                        </pre>
                      </div>
                      {error.component_stack && (
                        <div>
                          <p className="text-xs font-semibold text-gray-600 mb-1">Component Stack:</p>
                          <pre className="text-xs bg-gray-100 p-3 rounded overflow-auto max-h-32">
                            {error.component_stack}
                          </pre>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-4 text-xs">
                        <div>
                          <p className="font-semibold text-gray-600">Session ID:</p>
                          <code className="bg-gray-100 px-2 py-1 rounded">{error.session_id || '-'}</code>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-600">IP:</p>
                          <code className="bg-gray-100 px-2 py-1 rounded">{error.client_ip || '-'}</code>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-600">Idioma:</p>
                          <span>{error.browser_language || '-'}</span>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-600">Local Storage Keys:</p>
                          <span>{error.local_storage_keys?.join(', ') || '-'}</span>
                        </div>
                      </div>
                      {error.resolution_notes && (
                        <div className="bg-green-50 p-3 rounded">
                          <p className="text-xs font-semibold text-green-700">Notas de Resolución:</p>
                          <p className="text-sm">{error.resolution_notes}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FrontendErrorsDashboard;
